import React, { useState } from 'react'
import PropTypes from 'prop-types'
import formatISO from 'date-fns/formatISO'
import format from 'date-fns/format'
import subDays from 'date-fns/subDays'
import subWeeks from 'date-fns/subWeeks'
import subMonths from 'date-fns/subMonths'
import subQuarters from 'date-fns/subQuarters'
import subYears from 'date-fns/subYears'
import { Box } from '@maui/elements'
import { TimeRangeFilter } from '../TimeRangeFilter'

const formatDate = date => formatISO(date, { representation: 'date' })

const getStartDate = ({ range, value }) => {
  const fn = {
    day: subDays,
    week: subWeeks,
    month: subMonths,
    quarter: subQuarters,
    year: subYears,
  }[range]

  const start = fn(new Date(), Number(value))
  return formatDate(start)
}

const calculateTime = ({ range, value }) => ({
  start_date: getStartDate({ range, value }),
  end_date: formatDate(new Date()),
})

const DateRangeDisplay = ({ startDate, endDate }) => {
  if (!startDate) {
    return null
  }

  const f = dateVal => format(new Date(dateVal), 'PP')

  return (
    <Box fontSize="10px" color="colorTextDeemphasis">
      {`${f(startDate)} - ${f(endDate)}`}
    </Box>
  )
}

export const TimePickerDropdown = ({
  defaultNumber: defaultValue,
  defaultPeriod: defaultRange,
  hideRange,
  rangeOptions: ranges,
  setFilters,
  time,
  ...restProps
}) => {
  const [value, setValue] = useState(defaultValue)
  const [range, setRange] = useState(defaultRange)

  // The `updateData` callback is called on every change, and will be called with undefined
  // values if either value or range should revert to the default. The code here handles that by
  // falling back to default values if data.value or data.range is undefined.
  const updateData = data => {
    const updatedValue = data.value ?? defaultValue
    const updatedRange = data.range ?? defaultRange

    if (updatedValue !== value) { setValue(updatedValue) }
    if (updatedRange !== range) { setRange(updatedRange) }

    setFilters?.({
      range: updatedRange,
      value: updatedValue,
    })
  }

  return (
    <Box
      display="inline-flex"
      flexDirection="column"
      alignItems="flex-end"
    >
      <TimeRangeFilter
        editorOptions={{
          ranges,
          defaultValue,
          defaultRange,
          valueName: 'value',
          rangeName: 'range',
        }}
        data={{ value, range }}
        updateData={updateData}
      />
      {!hideRange && <DateRangeDisplay startDate={time.start_date} endDate={time.end_date} />}
    </Box>
  )
}

TimePickerDropdown.calculateTime = calculateTime

TimePickerDropdown.defaultProps = {
  defaultPeriod: 'month',
  defaultNumber: 1,
  rangeOptions: ['week', 'month', 'quarter'],
  hideRange: false,
}

TimePickerDropdown.propTypes = {
  time: PropTypes.shape({
    start_date: PropTypes.string,
    end_date: PropTypes.string,
  }),
  hideRange: PropTypes.bool,
  filters: PropTypes.object,
  setFilters: PropTypes.func,
  rangeOptions: PropTypes.array,
  defaultPeriod: PropTypes.oneOf(['day', 'week', 'month', 'quarter', 'year']),
  defaultNumber: PropTypes.number,
}
